import { useState } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Moon, Sparkles } from "lucide-react";

interface MoonPhase {
  name: string;
  emoji: string;
  description: string;
  rituals: string[];
  energy: string;
  color: string;
}

const moonPhases: Record<string, MoonPhase> = {
  new: {
    name: "Luna Nueva",
    emoji: "🌑",
    description: "Momento de nuevos comienzos y siembra de intenciones. La energía está en su punto más bajo, ideal para la introspección.",
    rituals: [
      "Escribir intenciones y metas para el ciclo lunar",
      "Meditación para conectar con tu interior",
      "Limpiar y ordenar espacios",
      "Plantar semillas (físicas o metafóricas)",
      "Ritual de manifestación con velas negras"
    ],
    energy: "Introspección y nuevos comienzos",
    color: "from-slate-700 to-slate-900"
  },
  waxingCrescent: {
    name: "Luna Creciente",
    emoji: "🌒",
    description: "La energía comienza a crecer. Es momento de tomar acción sobre las intenciones plantadas en luna nueva.",
    rituals: [
      "Afirmaciones positivas diarias",
      "Iniciar nuevos proyectos",
      "Ritual de atracción y abundancia",
      "Conectar con la naturaleza",
      "Visualización de objetivos"
    ],
    energy: "Crecimiento y determinación",
    color: "from-slate-600 to-slate-800"
  },
  firstQuarter: {
    name: "Cuarto Creciente",
    emoji: "🌓",
    description: "Momento de decisión y acción. La luna está a mitad de camino hacia su plenitud, trayendo desafíos que fortalecen.",
    rituals: [
      "Tomar decisiones importantes",
      "Superar obstáculos con determinación",
      "Ritual de coraje y fuerza",
      "Ejercicio físico intenso",
      "Resolver conflictos pendientes"
    ],
    energy: "Acción y superación de obstáculos",
    color: "from-indigo-700 to-slate-800"
  },
  waxingGibbous: {
    name: "Luna Gibosa Creciente",
    emoji: "🌔",
    description: "La energía está casi en su punto máximo. Momento para refinar, ajustar y prepararse para la culminación.",
    rituals: [
      "Revisar y ajustar planes",
      "Paciencia y refinamiento",
      "Rituales de prosperidad",
      "Conectar con la gratitud",
      "Preparación para la luna llena"
    ],
    energy: "Refinamiento y anticipación",
    color: "from-indigo-600 to-purple-800"
  },
  full: {
    name: "Luna Llena",
    emoji: "🌕",
    description: "La energía lunar está en su máximo poder. Momento de celebración, gratitud y liberación de lo que ya no sirve.",
    rituals: [
      "Cargar cristales bajo la luz lunar",
      "Ritual de liberación y soltar",
      "Baño ritual con sales y hierbas",
      "Meditación de luna llena",
      "Celebrar logros y dar gracias",
      "Escribir y quemar lo que deseas liberar"
    ],
    energy: "Plenitud, celebración y liberación",
    color: "from-yellow-500 to-amber-600"
  },
  waningGibbous: {
    name: "Luna Gibosa Menguante",
    emoji: "🌖",
    description: "La energía comienza a decrecer. Momento para compartir sabiduría, enseñar y reflexionar sobre lo aprendido.",
    rituals: [
      "Compartir conocimientos",
      "Prácticas de gratitud",
      "Ritual de agradecimiento",
      "Journaling reflexivo",
      "Enseñar o mentorear a otros"
    ],
    energy: "Gratitud y sabiduría compartida",
    color: "from-purple-600 to-indigo-800"
  },
  lastQuarter: {
    name: "Cuarto Menguante",
    emoji: "🌗",
    description: "Momento de soltar y dejar ir. La energía favorece la limpieza, el perdón y la preparación para el nuevo ciclo.",
    rituals: [
      "Limpieza energética del hogar",
      "Perdonar y soltar rencores",
      "Terminar proyectos pendientes",
      "Ritual de corte de lazos",
      "Desintoxicación física y mental"
    ],
    energy: "Liberación y perdón",
    color: "from-violet-700 to-slate-800"
  },
  waningCrescent: {
    name: "Luna Balsámica",
    emoji: "🌘",
    description: "La fase más oscura antes del renacimiento. Momento de descanso profundo, sanación y preparación interior.",
    rituals: [
      "Descanso y autocuidado",
      "Meditación profunda",
      "Sueños y trabajo onírico",
      "Preparar intenciones para luna nueva",
      "Ritual de sanación interior"
    ],
    energy: "Descanso, sanación y renovación",
    color: "from-slate-800 to-slate-950"
  }
};

const getMoonPhase = (date: Date): string => {
  // Simplified moon phase calculation
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  let c = 0;
  let e = 0;
  let jd = 0;
  let b = 0;

  if (month < 3) {
    c = year - 1;
    e = month + 12;
  } else {
    c = year;
    e = month;
  }

  jd = Math.floor(365.25 * c) + Math.floor(30.6001 * (e + 1)) + day - 694039.09;
  jd /= 29.5305882;
  b = Math.floor(jd);
  jd -= b;
  const phase = Math.round(jd * 8);

  switch (phase % 8) {
    case 0: return "new";
    case 1: return "waxingCrescent";
    case 2: return "firstQuarter";
    case 3: return "waxingGibbous";
    case 4: return "full";
    case 5: return "waningGibbous";
    case 6: return "lastQuarter";
    case 7: return "waningCrescent";
    default: return "new";
  }
};

const CalendarioLunar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const dayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty slots for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const days = getDaysInMonth(currentDate);
  const selectedPhase = selectedDate ? moonPhases[getMoonPhase(selectedDate)] : null;

  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date | null) => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const calendarSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Calendario Lunar - Fases de la Luna y Rituales",
    "description": "Calendario lunar mensual con fases de la luna, rituales y recomendaciones energéticas para cada fase lunar.",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web"
  };

  return (
    <>
      <Helmet>
        <title>Calendario Lunar 2026 - Fases de la Luna y Rituales | Dstino</title>
        <meta name="description" content="Calendario lunar mensual con todas las fases de la luna. Descubre rituales, energías y recomendaciones para luna nueva, llena, creciente y menguante." />
        <meta property="og:title" content="Calendario Lunar - Fases y Rituales" />
        <meta property="og:description" content="Calendario lunar con fases de la luna y rituales recomendados para cada fase." />
        <link rel="canonical" href={`${window.location.origin}/calendario-lunar`} />
      </Helmet>

      <SchemaMarkup schema={calendarSchema} />

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <Breadcrumb 
              items={[
                { name: "Inicio", url: "/" },
                { name: "Calendario Lunar" }
              ]}
            />

            {/* Hero Section */}
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-glow">
                🌙 Calendario Lunar 2026
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Descubre las fases de la luna y los rituales recomendados para aprovechar las energías lunares en tu vida diaria.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Calendar */}
              <Card className="p-4 md:p-6 gradient-celestial">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <Button variant="ghost" size="icon" onClick={prevMonth}>
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="text-center">
                    <h2 className="text-xl md:text-2xl font-bold">
                      {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={goToToday}
                      className="text-xs text-muted-foreground"
                    >
                      Ir a hoy
                    </Button>
                  </div>
                  <Button variant="ghost" size="icon" onClick={nextMonth}>
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((day) => (
                    <div key={day} className="text-center text-xs md:text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((date, index) => {
                    if (!date) {
                      return <div key={`empty-${index}`} className="aspect-square" />;
                    }

                    const phase = getMoonPhase(date);
                    const phaseData = moonPhases[phase];
                    const today = isToday(date);
                    const selected = isSelected(date);

                    return (
                      <button
                        key={date.toISOString()}
                        onClick={() => setSelectedDate(date)}
                        className={`
                          aspect-square rounded-lg flex flex-col items-center justify-center gap-0.5 
                          transition-all duration-200 relative group
                          ${today ? 'ring-2 ring-primary' : ''}
                          ${selected ? 'bg-primary/20 scale-105' : 'hover:bg-card/60'}
                        `}
                      >
                        <span className="text-lg md:text-xl">{phaseData.emoji}</span>
                        <span className={`text-xs ${today ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                          {date.getDate()}
                        </span>
                        
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-card border border-border rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 hidden md:block">
                          {phaseData.name}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Phase Legend */}
                <div className="mt-6 pt-4 border-t border-border/50">
                  <h3 className="text-sm font-medium mb-3 text-muted-foreground">Fases Lunares:</h3>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    {Object.entries(moonPhases).slice(0, 4).map(([key, phase]) => (
                      <div key={key} className="flex items-center gap-1">
                        <span>{phase.emoji}</span>
                        <span className="text-muted-foreground truncate">{phase.name.split(" ")[1] || phase.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Selected Phase Details */}
              <div className="space-y-4">
                {selectedPhase && selectedDate && (
                  <>
                    <Card className={`p-6 bg-gradient-to-br ${selectedPhase.color} border-0 text-white`}>
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-5xl md:text-6xl">{selectedPhase.emoji}</span>
                        <div>
                          <h2 className="text-2xl md:text-3xl font-bold">{selectedPhase.name}</h2>
                          <p className="text-white/80 text-sm">
                            {selectedDate.getDate()} de {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                          </p>
                        </div>
                      </div>
                      <p className="text-white/90 leading-relaxed">
                        {selectedPhase.description}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-sm text-white/80">
                        <Sparkles className="w-4 h-4" />
                        <span className="font-medium">Energía:</span> {selectedPhase.energy}
                      </div>
                    </Card>

                    <Card className="p-6 gradient-mystical">
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Moon className="w-5 h-5 text-primary" />
                        Rituales Recomendados
                      </h3>
                      <ul className="space-y-3">
                        {selectedPhase.rituals.map((ritual, index) => (
                          <li 
                            key={index} 
                            className="flex items-start gap-3 text-muted-foreground"
                          >
                            <span className="text-primary mt-0.5">✦</span>
                            <span>{ritual}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </>
                )}
              </div>
            </div>

            {/* All Moon Phases Overview */}
            <Card className="mt-8 p-6 md:p-8 gradient-celestial">
              <h2 className="text-2xl font-bold mb-6 text-center">
                Guía Completa de las Fases Lunares
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(moonPhases).map(([key, phase]) => (
                  <div 
                    key={key}
                    className="p-4 rounded-lg bg-card/50 border border-border/30 hover:border-primary/50 transition-colors"
                  >
                    <div className="text-3xl mb-2">{phase.emoji}</div>
                    <h3 className="font-bold text-sm mb-1">{phase.name}</h3>
                    <p className="text-xs text-muted-foreground">{phase.energy}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* SEO Content */}
            <Card className="mt-8 p-6 md:p-8 gradient-celestial">
              <h2 className="text-xl md:text-2xl font-bold mb-4">
                ¿Cómo usar el Calendario Lunar?
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  El calendario lunar es una herramienta ancestral que te permite sincronizar 
                  tus actividades con las energías de la luna. Cada fase lunar trae consigo 
                  una energía única que puedes aprovechar para potenciar diferentes aspectos de tu vida.
                </p>
                <p>
                  <strong className="text-foreground">Luna Nueva:</strong> Ideal para plantar intenciones y comenzar nuevos proyectos. 
                  <strong className="text-foreground"> Luna Llena:</strong> Momento de celebración, gratitud y liberación de lo que ya no sirve.
                  <strong className="text-foreground"> Cuarto Creciente:</strong> Tiempo de acción y superación de obstáculos.
                  <strong className="text-foreground"> Cuarto Menguante:</strong> Fase para soltar, perdonar y limpiar.
                </p>
                <p>
                  Selecciona cualquier día del calendario para ver la fase lunar correspondiente 
                  y los rituales recomendados para aprovechar al máximo las energías del momento.
                </p>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </>
  );
};

export default CalendarioLunar;
